/* eslint-disable no-unused-vars */
import { Tabs } from 'antd'
import { FormRangePicker } from 'Components'
import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { parse } from 'qs'
import PropTypes from 'prop-types'
import moment from 'moment'
import { useHistory, useLocation } from 'react-router-dom'
import { useDashboardMain } from 'Hooks/dashboardMain'
import { getLocalStorage, setLocalStorage, STORAGE } from 'Utils'
import { useAuth } from 'Hooks'
import SaleTabDashboard from './sale'
import UserActivityTabDashboard from './userActivity'
import VisitTabDashboard from './visit'

const { TabPane } = Tabs
const TabDashBoardStyled = styled.div`
    background-color: white;

    .ant-tabs-tab {
        padding: 16px 0;
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
    }
    .ant-tabs-nav-wrap {
        margin-left: 24px;
    }
`

const TabContent = styled.div`
    padding: 24px 20px;
    height: 344px;
    display: flex;
    flex-direction: column;
    justify-content: end;
`

const Operation = styled.div`
    margin-right: 24px;

    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 24px;

    cursor: pointer;

    .type-active {
      color: #1890FF
    }
`

export const listOperation = (trans) => [
  {
    title: trans('dashboard:allDay'),
    to: 'day',
    type: 'date',
    month: 1
  },
  {
    title: trans('dashboard:allWeek'),
    to: 'week',
    type: 'week',
    month: 2
  },
  {
    title: trans('dashboard:allMonth'),
    to: 'month',
    type: 'month',
    month: 11
  },
  {
    title: trans('dashboard:allYear'),
    to: 'year',
    type: 'year',
    month: 132
  }
]

export default function TabDashboard() {
  const [trans] = useTranslation(['dashboard'])
  const form = useForm({})
  const { watch, setValue } = form
  const history = useHistory()
  const { search } = useLocation()
  let { rangeType } = parse(search, { ignoreQueryPrefix: true })

  const [dates, setDates] = useState(null)
  const [monthRange, setMonthRange] = useState(null)

  const { dataChartTab, getDataTabChartAction, getDataDashboardAction } = useDashboardMain()
  const { logOutAction } = useAuth()

  const disabledDate = (current) => {
    if (!dates) {
      return false
    }
    const tooLate = dates[0] && current.diff(dates[0], 'months', true) > monthRange
    const tooEarly = dates[1] && dates[1].diff(current, 'months', true) > monthRange
    return !!tooEarly || !!tooLate
  }

  const getTypeLabel = (dataItem) => {
    switch (rangeType) {
      case 'day':
        return `${dataItem.date}/${dataItem.month}/${dataItem.year}`
      case 'week':
        return `${dataItem.week}/${dataItem.year}`
      case 'month':
        return `${dataItem.month}/${dataItem.year}`
      case 'year':
        return `${dataItem.year}`
      default:
        return null
    }
  }

  const onOpenChange = (open) => {
    if (open) {
      setDates([null, null])
      setValue('date', null)
    } else if (!open && watch('date')) {
      const condition = {
        type: rangeType,
        from: moment(watch('date')[0]).format('YYYY-MM-DD') || null,
        to: moment(watch('date')[1]).format('YYYY-MM-DD') || null
      }
      getDataTabChartAction(condition)
    }
  }

  useEffect(() => {
    if (rangeType) {
      const valueRangeType = listOperation(trans).find((e) => e.to === rangeType)
      setMonthRange(valueRangeType.month)
      const date = moment()
      getDataDashboardAction({
        year: date.year(),
        month: date.month() + 1,
        date: date.date(),
        type: rangeType,
        from: watch('date') ? moment(watch('date')[0]).format('YYYY-MM-DD') : moment().subtract(valueRangeType.month, 'months').format('YYYY-MM-DD'),
        to: watch('date') ? moment(watch('date')[1]).format('YYYY-MM-DD') : date.format('YYYY-MM-DD')
      })
      if (getLocalStorage(STORAGE.NOT_AUTHENTICATED_ERROR) === 'reset_token') {
        logOutAction()
        // setLocalStorage(STORAGE.NOT_AUTHENTICATED_ERROR, 'reset_token')
        history.push('/login')
      }
    } else {
      history.push('/dashboard?rangeType=month')
      setMonthRange(11)
    }
  }, [rangeType])

  const operations = (
    <Operation>
      {listOperation(trans).map((item) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span
          key={item.to}
          className={rangeType === item.to ? 'type-active' : ''}
          onClick={() => {
            setDates([null, null])
            setValue('date', null)
            history.push(`/dashboard?rangeType=${item.to}`)
          }}
        >
          {item.title}
        </span>
      ))}
      <span style={{ width: '256px' }}>
        <FormRangePicker
          name="date"
          onOpenChange={onOpenChange}
          disabledDate={disabledDate}
          onCalendarChange={(val) => {
            if (!val) {
              const condition = {
                type: rangeType,
                from: moment().subtract(monthRange, 'months').format('YYYY-MM-DD'),
                to: moment().format('YYYY-MM-DD')
              }
              getDataTabChartAction(condition)
            } else {
              setDates(val)
            }
          }}
        />
      </span>
    </Operation>
  )

  return (
    <TabDashBoardStyled>
      <FormProvider {...form}>
        <Tabs tabBarExtraContent={operations}>
          <TabPane tab={trans('dashboard:sales')} key="1">
            <TabContent>
              <SaleTabDashboard getTypeLabel={getTypeLabel} dataSalesTrend={dataChartTab?.salesTrend} />
            </TabContent>
          </TabPane>
          <TabPane tab={trans('dashboard:userActivity')} key="2">
            <TabContent>
              <UserActivityTabDashboard getTypeLabel={getTypeLabel} dataActivity={dataChartTab?.userActivity} />
            </TabContent>
          </TabPane>
          <TabPane tab={trans('dashboard:visits')} key="3">
            <TabContent>
              <VisitTabDashboard getTypeLabel={getTypeLabel} dataVisits={dataChartTab?.visits} />
            </TabContent>
          </TabPane>
        </Tabs>
      </FormProvider>
    </TabDashBoardStyled>
  )
}

TabDashboard.propTypes = {
  monthRange: PropTypes.number
}
